import React from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { useServiceWorker } from '../contexts/ServiceWorkerContext';

export function ServiceWorkerUpdateBadge({ className = '' }) {
  const { updateAvailable } = useServiceWorker();
  if (!updateAvailable) return null;

  return (
    <span
      aria-label="Sena update available"
      className={`absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-amber-300 ring-2 ring-[#4f7a64] ${className}`}
    />
  );
}

export default function ServiceWorkerUpdateAction({
  isCollapsed = false,
  surface = 'sidebar',
}) {
  const {
    updateAvailable,
    updateStatus,
    updateError,
    applyUpdate,
  } = useServiceWorker();

  if (!updateAvailable) return null;

  const isApplying = updateStatus === 'applying';
  const hasError = updateStatus === 'error';
  const label = isApplying ? 'Updating Sena…' : hasError ? 'Retry update' : 'Update Sena';
  const title = hasError
    ? updateError
    : 'Save your current work before updating Sena.';
  const isMenu = surface === 'menu';

  return (
    <button
      type="button"
      onClick={applyUpdate}
      disabled={isApplying}
      aria-label={isCollapsed ? label : undefined}
      title={title}
      className={isMenu
        ? 'flex w-full items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 text-left text-amber-950 hover:bg-amber-100 disabled:cursor-wait disabled:opacity-75'
        : `flex w-full items-center gap-2 rounded-md border border-amber-300/70 bg-amber-300/15 px-3 py-2 text-left text-amber-100 transition hover:bg-amber-300/25 disabled:cursor-wait disabled:opacity-75 ${isCollapsed ? 'justify-center' : ''}`}
    >
      <span className="relative flex-shrink-0">
        <FiRefreshCw className={`h-4 w-4 ${isApplying ? 'animate-spin' : ''}`} />
        {!isApplying && (
          <span className={`absolute -right-1 -top-1 h-2 w-2 rounded-full ${hasError ? 'bg-red-400' : 'bg-amber-300'}`} />
        )}
      </span>
      {!isCollapsed && (
        <span className="min-w-0">
          <span className="block text-sm font-semibold">{label}</span>
          <span className={`block text-xs ${isMenu ? 'text-amber-800' : 'text-amber-100/80'}`}>
            {hasError ? updateError : 'Save your work before clicking.'}
          </span>
        </span>
      )}
    </button>
  );
}

export function ServiceWorkerUpdateBanner() {
  const { updateAvailable } = useServiceWorker();
  if (!updateAvailable) return null;
  return <aside aria-label="Sena update available" className="fixed bottom-4 right-4 z-[95] max-w-sm rounded-lg border border-amber-300 bg-amber-50 p-3 shadow-lg">
    <p className="mb-2 text-sm text-amber-950">A new version of Sena is ready. Save your work, then update.</p>
    <ServiceWorkerUpdateAction surface="menu" />
  </aside>;
}
